import Phaser from "phaser";
import i18next from "i18next";
import { WindowManager } from "../../../ui/window-manager";
import { ModernButton } from "../../../ui/modern-button";
import { showConfetti } from "../../../ui/splash-particle";
import { fetchItemDefs } from "../../../item-catalog";
import { ItemDefinition } from "../../../models/common";
import { SpriteLoader } from "../../../sprite-loader";

// The gauntlet is a run of consecutive successful extractions. It starts at 100
// survivors (10 icons, each icon = 10) sitting on the first island. Every successful
// extraction, a random 60-80% of the current survivors "jump" over to the next
// island - the rest are lost along the way. A failed extraction while a run is
// active dumps every remaining survivor into the mud and ends the run.
export type GauntletState = { count: number; islandIndex: number };

const STORAGE_KEY = "gauntletRunState";
const MAX_COUNT = 10; // 10 icons == 100 survivors
const MIN_SURVIVE_PCT = 60;
const MAX_SURVIVE_PCT = 80;

// Island anchor points as fractions of the bordered window's content rect, matching
// the dotted path drawn in gauntlet-bg.png: the "START" island (bottom-left) through
// five stepping-stone islands up to the treasure chest island (bottom-right).
const ISLAND_POSITIONS: { x: number; y: number }[] = [
  { x: 0.134, y: 0.582 }, // START
  { x: 0.256, y: 0.374 },
  { x: 0.375, y: 0.251 },
  { x: 0.507, y: 0.4 },
  { x: 0.664, y: 0.427 },
  { x: 0.793, y: 0.582 },
  { x: 0.921, y: 0.704 }, // treasure chest
];
const LAST_ISLAND_INDEX = ISLAND_POSITIONS.length - 1;

// Item rarity color bands, matching season-screen's item-reveal cards (see
// ITEM_RARITY_COLOR in season-screen.ts — duplicated here rather than shared since
// that one isn't exported).
const GAUNTLET_ITEM_RARITY_COLOR = { common: 0x9ca3af, uncommon: 0x4ade80, rare: 0x60a5fa, epic: 0xc084fc };
function getGauntletItemRarityColor(price: number): number {
  if (price <= 20) return GAUNTLET_ITEM_RARITY_COLOR.common;
  if (price <= 100) return GAUNTLET_ITEM_RARITY_COLOR.uncommon;
  if (price <= 250) return GAUNTLET_ITEM_RARITY_COLOR.rare;
  return GAUNTLET_ITEM_RARITY_COLOR.epic;
}

type GauntletLootResponse = {
  success: boolean;
  items?: { id: number }[];
  droppedToGround?: boolean;
  error?: string;
};

export function loadGauntletState(): GauntletState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.count === "number" && typeof parsed?.islandIndex === "number") return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveGauntletState(state: GauntletState | null): void {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode etc.) - the run just won't persist across matches.
  }
}

export function hasActiveGauntletRun(): boolean {
  return loadGauntletState() !== null;
}

export class GauntletScreen {
  private readonly scene: Phaser.Scene;
  private readonly panel: Phaser.GameObjects.Container;
  private readonly windowManager: WindowManager;

  private visible: boolean = false;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private activeTimers: Phaser.Time.TimerEvent[] = [];

  private windowX = 0;
  private windowY = 0;
  private windowW = 0;
  private windowH = 0;

  // Icon 0 always represents the player themself (white outline ring) rather than one
  // of the anonymous crowd - and is always among the survivors on a successful jump,
  // since survivorCount (see animateJump) is never less than 1.
  private readonly PLAYER_ICON_INDEX = 0;

  constructor(scene: Phaser.Scene, panel: Phaser.GameObjects.Container, windowManager: WindowManager) {
    this.scene = scene;
    this.panel = panel;
    this.windowManager = windowManager;
  }

  static preload(scene: Phaser.Scene): void {
    if (!scene.textures.exists("gauntlet-bg")) {
      scene.load.image("gauntlet-bg", "/sprites/base/gauntlet-bg.png");
    }
    if (!scene.textures.exists("gauntlet-logo")) {
      scene.load.image("gauntlet-logo", "/sprites/base/gauntlet-logo.png");
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  // Read-only view of the current run's progress — opened on demand (e.g. from the
  // gauntlet widget on the quests screen), with no jump/fail animation and an
  // immediately-clickable close button.
  show(onClose: () => void): void {
    const state = loadGauntletState() ?? { count: MAX_COUNT, islandIndex: 0 };
    this.beginShow();
    this.renderGroup(state.count, state.islandIndex);
    this.buildContinueButton(() => this.finish(onClose), true, "Close");
  }

  showExtractionSuccess(onDismiss: () => void): void {
    const prev = loadGauntletState() ?? { count: MAX_COUNT, islandIndex: 0 };
    this.beginShow();

    const { icons: startIcons, label: startLabel } = this.renderGroup(prev.count, prev.islandIndex);

    const survivePct = Phaser.Math.Between(MIN_SURVIVE_PCT, MAX_SURVIVE_PCT);
    const nextCount = Phaser.Math.Clamp(Math.round((prev.count * survivePct) / 100), 1, MAX_COUNT);
    const nextIslandIndex = Math.min(prev.islandIndex + 1, LAST_ISLAND_INDEX);
    const next: GauntletState = { count: nextCount, islandIndex: nextIslandIndex };
    // Only the jump that actually lands on the treasure island triggers the chest —
    // not every subsequent win once already parked there (that can't happen anyway
    // since reaching the chest resets the run, but this keeps the check meaningful
    // even if that reset logic ever changes).
    const reachedTreasureIsland = nextIslandIndex === LAST_ISLAND_INDEX && prev.islandIndex !== LAST_ISLAND_INDEX;

    const continueButton = this.buildContinueButton(() => this.finish(onDismiss), false);

    const timer = this.scene.time.delayedCall(500, () => {
      this.animateJump(startIcons, startLabel, prev, next, () => {
        saveGauntletState(next);
        if (reachedTreasureIsland) {
          continueButton.destroy();
          this.showChestReveal(() => {
            saveGauntletState(null);
            this.finish(onDismiss);
          });
          return;
        }
        continueButton.setDisabled(false);
      });
    });
    this.activeTimers.push(timer);
  }

  showExtractionFailure(onDismiss: () => void): void {
    const prev = loadGauntletState();
    this.beginShow();

    if (!prev) {
      // Nothing to lose - still let the caller proceed through the normal flow.
      this.finish(onDismiss);
      return;
    }

    const { icons, label } = this.renderGroup(prev.count, prev.islandIndex);
    saveGauntletState(null);

    const continueButton = this.buildContinueButton(() => this.finish(onDismiss), false);

    const timer = this.scene.time.delayedCall(500, () => {
      this.animateFail(icons, label, prev.islandIndex, () => {
        this.windowManager.showNotification("Gauntlet failed", "error", "GAUNTLET");
        continueButton.setDisabled(false);
      });
    });
    this.activeTimers.push(timer);
  }

  // Reaching the treasure island: show a closed chest with an OPEN button: tapping it
  // wiggles the chest (same tween shape as the season screen's loot-box open), then
  // asks the server for the rolled "Gauntlet" container loot and reveals the items —
  // mirroring season-screen.ts's openBox()/revealLootBoxItems() flow.
  private showChestReveal(onClaimed: () => void): void {
    const pos = this.islandPos(LAST_ISLAND_INDEX);
    const rewardContainer = this.scene.add.container(0, 0);
    this.panel.add(rewardContainer);

    // Frame 1 of the "lootbox-chests" spritesheet (chests.png, 32x32 frames, 12 cols).
    const chest = this.scene.add.sprite(pos.x, pos.y, "lootbox-chests", 1).setDisplaySize(64, 64);
    rewardContainer.add(chest);

    let isOpening = false;
    const openButton = new ModernButton({
      scene: this.scene,
      x: this.windowX + this.windowW / 2,
      y: this.windowY + this.windowH - 36,
      text: "OPEN",
      style: "primary",
      onClick: () => {
        if (isOpening) return;
        isOpening = true;
        // Destroyed rather than just disabled — the eventual "Continue" button reuses
        // this same bottom-center slot once the reveal finishes.
        openButton.destroy();
        this.openGauntletChest(chest, rewardContainer, onClaimed);
      },
    });
    this.panel.add(openButton);
  }

  private openGauntletChest(
    chest: Phaser.GameObjects.Sprite,
    rewardContainer: Phaser.GameObjects.Container,
    onClaimed: () => void,
  ): void {
    // Raced against a timeout so a dropped connection or missing ack can't leave the
    // player stuck on a hidden chest with no way to continue.
    const responsePromise = new Promise<GauntletLootResponse>((resolve) => {
      let settled = false;
      const finish = (response: GauntletLootResponse) => {
        if (settled) return;
        settled = true;
        resolve(response);
      };
      window.socket?.emit("request-gauntlet-loot", {}, finish);
      const timeout = this.scene.time.delayedCall(6000, () => finish({ success: false, error: "timeout" }));
      this.activeTimers.push(timeout);
    });

    const wiggle = this.scene.tweens.add({
      targets: chest,
      angle: { from: -4, to: 4 },
      duration: 150,
      yoyo: true,
      repeat: 2,
      ease: "Sine.easeInOut",
      onComplete: async () => {
        const response = await responsePromise;

        showConfetti(this.scene);
        const hideChest = this.scene.tweens.add({
          targets: chest,
          scale: 0,
          alpha: 0,
          duration: 200,
          ease: "Back.easeIn",
        });
        this.activeTweens.push(hideChest);

        await this.revealGauntletLoot(response, rewardContainer);

        this.buildContinueButton(onClaimed, true, "Continue");
      },
    });
    this.activeTweens.push(wiggle);
  }

  private async revealGauntletLoot(
    response: GauntletLootResponse,
    rewardContainer: Phaser.GameObjects.Container,
  ): Promise<void> {
    const centerX = this.windowX + this.windowW / 2;
    const centerY = this.windowY + this.windowH / 2;

    if (!response.success || !response.items || response.items.length === 0) {
      const fallback = this.scene.add
        .text(centerX, centerY, "No loot this time", {
          fontSize: "16px",
          fontFamily: "Anton",
          color: "#e2e8f0",
          stroke: "#000000",
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setAlpha(0);
      rewardContainer.add(fallback);
      const t = this.scene.tweens.add({ targets: fallback, alpha: 1, duration: 300 });
      this.activeTweens.push(t);
      return;
    }

    const allItemDefs = await fetchItemDefs();
    const entries = response.items
      .map((entry) => allItemDefs.find((d) => d.id === entry.id))
      .filter((def): def is ItemDefinition => !!def);

    if (entries.length === 0) {
      const fallback = this.scene.add
        .text(centerX, centerY, "Items added to storage", {
          fontSize: "14px",
          fontFamily: "Anton",
          color: "#94a3b8",
        })
        .setOrigin(0.5)
        .setAlpha(0);
      rewardContainer.add(fallback);
      const t = this.scene.tweens.add({ targets: fallback, alpha: 1, duration: 300 });
      this.activeTweens.push(t);
      return;
    }

    const ITEM_STEP_X = 84;
    const ITEM_STEP_Y = 112;
    const ITEMS_PER_ROW = 3;
    const rows = Math.ceil(entries.length / ITEMS_PER_ROW);
    const startY = centerY - ((rows - 1) * ITEM_STEP_Y) / 2;

    entries.forEach((def, i) => {
      const row = Math.floor(i / ITEMS_PER_ROW);
      const col = i % ITEMS_PER_ROW;
      const itemsInRow = Math.min(ITEMS_PER_ROW, entries.length - row * ITEMS_PER_ROW);
      const rowStartX = centerX - ((itemsInRow - 1) * ITEM_STEP_X) / 2;

      const card = this.buildGauntletItemCard(def, rowStartX + col * ITEM_STEP_X, startY + row * ITEM_STEP_Y);
      card.setScale(0);
      rewardContainer.add(card);
      const t = this.scene.tweens.add({
        targets: card,
        scale: 1,
        duration: 400,
        delay: i * 120,
        ease: "Elastic.out",
      });
      this.activeTweens.push(t);
    });
  }

  private buildGauntletItemCard(def: ItemDefinition, x: number, y: number): Phaser.GameObjects.Container {
    const w = 72;
    const h = 100;
    const color = getGauntletItemRarityColor(def.price);
    const c = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x111827, 0.9);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(5, 0x000000, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(2, color, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    c.add(bg);

    if (this.scene.textures.exists(def.spriteSheet)) {
      const sprite = SpriteLoader.createSprite(this.scene, def.spriteSheet, def.spriteX, def.spriteY, 0, -14, true);
      c.add(sprite);
    }

    c.add(
      this.scene.add
        .text(0, h / 2 - 34, def.name, {
          fontSize: "16px",
          fontFamily: "Anton, sans-serif",
          color: "#e2e8f0",
          align: "center",
          wordWrap: { width: w - 6 },
          lineSpacing: 2,
          stroke: "#000000",
          strokeThickness: 2,
        })
        .setOrigin(0.5, 0),
    );

    return c;
  }

  hide(): void {
    this.cleanupTimersAndTweens();
    this.panel.removeAll(true);
    this.panel.setVisible(false);
    this.visible = false;
  }

  destroy(): void {
    this.hide();
  }

  // ── Shared setup ─────────────────────────────────────────────────────────────

  private beginShow(): void {
    this.panel.setVisible(true);
    this.panel.removeAll(true);
    this.cleanupTimersAndTweens();
    this.visible = true;

    const { width, height } = this.scene.scale;

    const dim = this.scene.add.graphics();
    dim.fillStyle(0x000000, 0.65);
    dim.fillRect(0, 0, width, height);
    this.panel.add(dim);

    const margin = 20;
    this.windowX = margin;
    this.windowY = margin;
    this.windowW = width - margin * 2;
    this.windowH = height - margin * 2;

    this.buildWindowFrame();
    this.buildTitle();
    this.buildHint();
  }

  // Sits just above the bottom-center button slot shared by Continue/Close/OPEN.
  private buildHint(): void {
    const hint = this.scene.add
      .text(
        this.windowX + this.windowW / 2,
        this.windowY + this.windowH - 72,
        i18next.t("ui:instructions.gauntletHint").toUpperCase(),
        {
          fontSize: "18px",
          fontFamily: "Anton",
          color: "#ffffff",
          align: "center",
          stroke: "#000000",
          strokeThickness: 4,
          wordWrap: { width: this.windowW - 40 },
        },
      )
      .setOrigin(0.5, 1);
    this.panel.add(hint);
  }

  private buildWindowFrame(): void {
    const { windowX: x, windowY: y, windowW: w, windowH: h } = this;

    if (this.scene.textures.exists("gauntlet-bg")) {
      const bg = this.scene.add.image(x + w / 2, y + h / 2, "gauntlet-bg");
      const scale = Math.max(w / bg.width, h / bg.height);
      bg.setScale(scale);

      const maskShape = this.scene.add.graphics();
      maskShape.fillStyle(0xffffff);
      maskShape.fillRect(x, y, w, h);
      maskShape.setVisible(false);
      this.panel.add(maskShape);
      bg.setMask(maskShape.createGeometryMask());

      this.panel.add(bg);
    } else {
      const fallback = this.scene.add.graphics();
      fallback.fillStyle(0x0f2e52, 1);
      fallback.fillRect(x, y, w, h);
      this.panel.add(fallback);
    }

    const border = this.scene.add.graphics();
    border.lineStyle(6, 0x000000, 1);
    border.strokeRect(x, y, w, h);
    border.lineStyle(3, 0xf59e0b, 1);
    border.strokeRect(x + 3, y + 3, w - 6, h - 6);
    this.panel.add(border);
  }

  private buildTitle(): void {
    const logoX = this.windowX + this.windowW / 2;
    const logoY = this.windowY + 12;
    const maxLogoW = this.windowW * 0.5;

    // Drop shadow — an offset, tinted, semi-transparent copy behind the real logo
    // (same technique as drawWidgetBackground's card shadows), rather than a preFX
    // shadow pipeline, since that only works under WebGL and this project runs AUTO.
    const shadow = this.scene.add.image(logoX + 4, logoY + 5, "gauntlet-logo");
    shadow.setTint(0x000000);
    shadow.setAlpha(0.5);
    this.panel.add(shadow);

    const logo = this.scene.add.image(logoX, logoY, "gauntlet-logo");
    this.panel.add(logo);

    const scale = Math.min(1, maxLogoW / logo.width) * 0.8;
    shadow.setScale(scale);
    logo.setScale(scale);
  }

  private buildContinueButton(onClick: () => void, enabledImmediately: boolean, label: string = "Continue"): ModernButton {
    const button = new ModernButton({
      scene: this.scene,
      x: this.windowX + this.windowW / 2,
      y: this.windowY + this.windowH - 36,
      text: label,
      style: "secondary",
      disabled: !enabledImmediately,
      onClick,
    });
    this.panel.add(button);
    return button;
  }

  private finish(onDismiss: () => void): void {
    this.hide();
    onDismiss();
  }

  // ── Islands / icons ──────────────────────────────────────────────────────────

  private islandPos(index: number): { x: number; y: number } {
    const frac = ISLAND_POSITIONS[Phaser.Math.Clamp(index, 0, LAST_ISLAND_INDEX)];
    return {
      x: this.windowX + this.windowW * frac.x,
      y: this.windowY + this.windowH * frac.y,
    };
  }

  // The background is dirt/mud everywhere off the islands, so "falling in the mud"
  // just means dropping icons a short distance below whichever island they were on.
  private mudPos(index: number): { x: number; y: number } {
    const islandCenter = this.islandPos(index);
    return { x: islandCenter.x, y: Math.min(islandCenter.y + 55, this.windowY + this.windowH - 20) };
  }

  // Lays out `count` player icons (1-10) as a small crowd centered on the given
  // island, plus a "<count*10>" label underneath. Returns the created objects so
  // callers can animate/replace them.
  private renderGroup(
    count: number,
    islandIndex: number,
  ): { icons: Phaser.GameObjects.Container[]; label: Phaser.GameObjects.Text } {
    const pos = this.islandPos(islandIndex);
    const icons = this.layoutIcons(count, pos.x, pos.y);
    icons.forEach((icon) => this.panel.add(icon));

    const label = this.buildCountLabel(count, pos.x, pos.y);
    this.panel.add(label);

    return { icons, label };
  }

  private layoutIcons(count: number, centerX: number, centerY: number): Phaser.GameObjects.Container[] {
    const perRow = 5;
    const spacingX = 14;
    const spacingY = 16;
    const rows = Math.ceil(count / perRow);
    const icons: Phaser.GameObjects.Container[] = [];

    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / perRow);
      const col = i % perRow;
      const colsInRow = Math.min(perRow, count - row * perRow);
      const rowOffsetX = ((colsInRow - 1) * spacingX) / 2;
      const rowOffsetY = ((rows - 1) * spacingY) / 2;
      const x = centerX - rowOffsetX + col * spacingX;
      const y = centerY - rowOffsetY + row * spacingY;

      const container = this.scene.add.container(x, y);

      if (i === this.PLAYER_ICON_INDEX) {
        const outline = this.scene.add.circle(0, 0, 11);
        outline.setStrokeStyle(2, 0xffffff, 1);
        container.add(outline);
      }

      // Frame 0 of the "player" spritesheet is the default player skin's first frame.
      const sprite = this.scene.add.sprite(0, 0, "player", 0).setDisplaySize(18, 18).setOrigin(0.5);
      container.add(sprite);

      icons.push(container);
    }

    return icons;
  }

  private buildCountLabel(count: number, centerX: number, aboveY: number): Phaser.GameObjects.Text {
    return this.scene.add
      .text(centerX, aboveY + 26, `${count * 10}`, {
        fontSize: "18px",
        fontFamily: "Anton",
        color: "#ffffff",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5);
  }

  // ── Animations ───────────────────────────────────────────────────────────────

  // Two sequential phases so the outcome reads clearly: everyone who doesn't make the
  // jump falls into the mud first, then (once all of that has settled) the survivors
  // jump to the next island.
  private animateJump(
    startIcons: Phaser.GameObjects.Container[],
    startLabel: Phaser.GameObjects.Text,
    prev: GauntletState,
    next: GauntletState,
    onComplete: () => void,
  ): void {
    const targetPos = this.islandPos(next.islandIndex);
    const survivorCount = startIcons.length <= next.count ? startIcons.length : next.count;
    const jumpApexOffset = -40;

    startLabel.destroy();

    const survivors = startIcons.slice(0, survivorCount);
    const failed = startIcons.slice(survivorCount);

    const landAndFinish = () => {
      // Hold briefly once everyone has landed so the survivors are clearly visible on
      // their new island before the scene moves on.
      const holdTimer = this.scene.time.delayedCall(400, () => {
        startIcons.forEach((icon) => icon.destroy());
        const { icons: landedIcons } = this.renderGroup(next.count, next.islandIndex);
        landedIcons.forEach((icon) => {
          icon.setScale(0.4);
          const tween = this.scene.tweens.add({
            targets: icon,
            scale: 1,
            duration: 180,
            ease: "Back.easeOut",
          });
          this.activeTweens.push(tween);
        });
        onComplete();
      });
      this.activeTimers.push(holdTimer);
    };

    const animateSurvivors = () => {
      if (survivors.length === 0) {
        landAndFinish();
        return;
      }
      let remaining = survivors.length;
      const onLanded = () => {
        remaining--;
        if (remaining <= 0) landAndFinish();
      };

      survivors.forEach((icon, i) => {
        const delay = i * 60;
        const upTween = this.scene.tweens.add({
          targets: icon,
          x: targetPos.x,
          y: targetPos.y + jumpApexOffset,
          duration: 260,
          delay,
          ease: "Sine.easeOut",
          onComplete: () => {
            const downTween = this.scene.tweens.add({
              targets: icon,
              y: targetPos.y,
              duration: 180,
              ease: "Cubic.easeIn",
              onComplete: onLanded,
            });
            this.activeTweens.push(downTween);
          },
        });
        this.activeTweens.push(upTween);
      });
    };

    if (failed.length === 0) {
      animateSurvivors();
      return;
    }

    let failedRemaining = failed.length;
    const onFallen = () => {
      failedRemaining--;
      if (failedRemaining <= 0) animateSurvivors();
    };

    failed.forEach((icon, i) => {
      // Doesn't make the jump - hops partway, then falls short into the mud below
      // the island it jumped from (same mud treatment as a failed extraction),
      // instead of just fading away in mid-air.
      const mud = this.mudPos(prev.islandIndex);
      const midX = Phaser.Math.Linear(icon.x, targetPos.x, 0.25);
      const delay = i * 60;
      const upTween = this.scene.tweens.add({
        targets: icon,
        x: midX,
        y: icon.y + jumpApexOffset * 0.5,
        duration: 200,
        delay,
        ease: "Sine.easeOut",
        onComplete: () => {
          const downTween = this.scene.tweens.add({
            targets: icon,
            x: mud.x + Phaser.Math.Between(-16, 16),
            y: mud.y + Phaser.Math.Between(-6, 6),
            angle: Phaser.Math.Between(-100, 100),
            alpha: 0.7,
            duration: 300,
            ease: "Cubic.easeIn",
            onComplete: onFallen,
          });
          this.activeTweens.push(downTween);
        },
      });
      this.activeTweens.push(upTween);
    });
  }

  private animateFail(
    icons: Phaser.GameObjects.Container[],
    label: Phaser.GameObjects.Text,
    islandIndex: number,
    onComplete: () => void,
  ): void {
    let remaining = icons.length;
    const settle = () => {
      remaining--;
      if (remaining > 0) return;
      onComplete();
    };

    const fadeLabel = this.scene.tweens.add({
      targets: label,
      alpha: 0,
      duration: 300,
    });
    this.activeTweens.push(fadeLabel);

    icons.forEach((icon, i) => {
      const mud = this.mudPos(islandIndex);
      const tween = this.scene.tweens.add({
        targets: icon,
        x: icon.x + Phaser.Math.Between(-16, 16),
        y: mud.y + Phaser.Math.Between(-6, 6),
        angle: Phaser.Math.Between(-100, 100),
        alpha: 0.6,
        duration: 420,
        delay: i * 50,
        ease: "Cubic.easeIn",
        onComplete: settle,
      });
      this.activeTweens.push(tween);
    });
  }

  private cleanupTimersAndTweens(): void {
    this.activeTimers.forEach((t) => t.remove(false));
    this.activeTimers = [];
    this.activeTweens.forEach((t) => t.stop());
    this.activeTweens = [];
  }
}
